import type { Dropoff, ShiftDropoff } from "@domain/CRM/dropoff";

import { interleaveStopRowKeys, parseRowKey } from "./stopPlanning";

/**
 * One display row of a shift's run sequence: a job or a dropoff stop, interleaved on the
 * shared `Order` axis (MC's mixed sequence — SchedulingList.tsx:722-737).
 */
export type ShiftStopRow = { kind: "job"; jobId: string } | { kind: "dropoff"; stop: ShiftDropoff };

/** Stable row identity for React keys / DnD / staged ordering. */
export function shiftRowKey(row: ShiftStopRow): string {
	return row.kind === "job" ? `job:${row.jobId}` : `stop:${row.stop.id}`;
}

/**
 * Interleave a shift's dropoff stops into its displayed job sequence.
 *
 * The job sequence is authoritative and never reordered — it already reflects staged edits
 * (or the committed link order), while `Jobs.Order` may lag. Each stop slots in before the
 * first job whose persisted order exceeds the stop's (a job without an order counts as
 * last, and an order tie keeps the job first — both MC sort semantics); stops without an
 * order append at the very end, in input order.
 */
export function buildShiftRows(
	orderedJobIds: readonly string[],
	orderByJobId: ReadonlyMap<string, number | undefined>,
	stops: readonly ShiftDropoff[],
): ShiftStopRow[] {
	// Interleave at the row-key level (the shared MC ordering rule), then map keys back to the
	// rich display rows the view needs. Keeping the interleave in one place means the save
	// path's committed-sequence fallback can't drift from what the board shows.
	const stopById = new Map(stops.map((stop) => [stop.id, stop]));
	return interleaveStopRowKeys(orderedJobIds, orderByJobId, stops).map((key) => {
		const parsed = parseRowKey(key);
		if (parsed.kind === "job") return { kind: "job", jobId: parsed.id };
		return { kind: "dropoff", stop: stopById.get(parsed.id)! };
	});
}

/**
 * Fill each stop's display lookups (name/coordinates) from its linked master dropoff when the
 * junction record lacks them — freshly created stops have no Airtable lookups (the offline fake
 * never computes them, and Airtable's can lag a read). Fully-resolved stops pass through by
 * reference.
 */
export function resolveStopLookups(stops: readonly ShiftDropoff[], dropoffs: readonly Dropoff[]): ShiftDropoff[] {
	const masterById = new Map(dropoffs.map((d) => [d.id, d]));
	return stops.map((stop) => {
		if (stop.name && stop.latitude !== undefined && stop.longitude !== undefined) return stop;
		const master = stop.dropoffId ? masterById.get(stop.dropoffId) : undefined;
		if (!master) return stop;
		return {
			...stop,
			name: stop.name || master.name,
			latitude: stop.latitude ?? master.latitude,
			longitude: stop.longitude ?? master.longitude,
		} as ShiftDropoff;
	});
}

/** Stops bucketed by their shift (unlinked stops dropped), preserving input order. */
export function groupStopsByShift(stops: readonly ShiftDropoff[]): Map<string, ShiftDropoff[]> {
	const grouped = new Map<string, ShiftDropoff[]>();
	for (const stop of stops) {
		if (!stop.shiftId) continue;
		const bucket = grouped.get(stop.shiftId);
		if (bucket) bucket.push(stop);
		else grouped.set(stop.shiftId, [stop]);
	}
	return grouped;
}
